import {
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  InputRightElement,
  InputGroup,
  Flex,
  InputProps,
} from '@chakra-ui/react';
import { MaskitoOptions } from '@maskito/core';
import { maskitoNumberOptionsGenerator } from '@maskito/kit';
import { useMaskito } from '@maskito/react';
import React, { RefCallback } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { StrictFieldValues, StringPath } from './types';

type MaskTypes =
  | 'usd'
  | 'percentage'
  | 'number'
  | 'integer'
  | 'positiveNumber'
  | 'positiveInteger'
  | 'none';

const USD_MASK_OPTIONS = maskitoNumberOptionsGenerator({
  decimalZeroPadding: true,
  precision: 2,
  thousandSeparator: ',',
  decimalSeparator: '.',
  min: 0,
  prefix: '$',
});

const PERCENTAGE_MASK_OPTIONS = maskitoNumberOptionsGenerator({
  precision: Infinity,
  decimalSeparator: '.',
  min: 0,
  max: 100,
  postfix: '%',
});

const NUMBER_MASK_OPTIONS = maskitoNumberOptionsGenerator({
  precision: Infinity,
  thousandSeparator: ',',
});

const INTEGER_MASK_OPTIONS = maskitoNumberOptionsGenerator({
  precision: 0,
  thousandSeparator: ',',
});

const POSITIVE_NUMBER_MASK_OPTIONS = maskitoNumberOptionsGenerator({
  precision: Infinity,
  min: 0,
  thousandSeparator: ',',
});

const POSITIVE_INTEGER_MASK_OPTIONS = maskitoNumberOptionsGenerator({
  precision: 0,
  min: 0,
  thousandSeparator: ',',
});

const OPTIONS_MAP: Record<MaskTypes, MaskitoOptions | undefined> = {
  usd: USD_MASK_OPTIONS,
  percentage: PERCENTAGE_MASK_OPTIONS,
  number: NUMBER_MASK_OPTIONS,
  integer: INTEGER_MASK_OPTIONS,
  positiveNumber: POSITIVE_NUMBER_MASK_OPTIONS,
  positiveInteger: POSITIVE_INTEGER_MASK_OPTIONS,
  none: undefined,
};

const REQUIRED_FIELD_RULES = {
  required: { value: true, message: 'This is a required field.' },
};

const TEXT_INPUT_RULES_MAP = {
  usd: {
    ...REQUIRED_FIELD_RULES,
    pattern: {
      value: /^\$\d+/, // This regex prevents react-hook-form from interpreting '$' as a valid value.
      message: 'Enter a valid dollar amount.',
    },
  },
  percentage: {
    ...REQUIRED_FIELD_RULES,
    pattern: {
      value: /\d+%$/, // This regex prevents react-hook-form from interpreting '%' as a valid value.
      message: 'Enter a valid percentage.',
    },
  },
  number: REQUIRED_FIELD_RULES,
  integer: REQUIRED_FIELD_RULES,
  positiveNumber: REQUIRED_FIELD_RULES,
  positiveInteger: REQUIRED_FIELD_RULES,
  none: REQUIRED_FIELD_RULES,
};

export type TextInputProps<T extends StrictFieldValues> = {
  name: StringPath<T>;
  label?: string;
  placeholder?: string;
  defaultValue?: string;
  mask?: MaskTypes;
  required?: boolean;
  helperText?: React.ReactNode;
  inputRightElement?: React.ReactNode;
  isDisabled?: boolean;
  tag?: React.ReactNode;
  inputProps?: InputProps;
};

export const TextInput = <T extends StrictFieldValues>({
  name,
  label,
  placeholder = '',
  defaultValue,
  mask = 'none',
  required,
  helperText = '',
  inputRightElement,
  isDisabled,
  tag,
  inputProps,
}: TextInputProps<T>) => {
  const { control } = useFormContext<T>();
  const options = OPTIONS_MAP[mask];
  const maskitoRef = useMaskito({ options });
  const rules = required ? TEXT_INPUT_RULES_MAP[mask] : undefined;

  return (
    <Controller
      name={name}
      control={control}
      // Can cast to any here because StringPath ensures that the type of this
      // field is always a string.
      defaultValue={defaultValue as any}
      rules={rules}
      render={({ field, fieldState: { error } }) => {
        const ref: RefCallback<HTMLElement | null> = (node): void => {
          field.ref(node);
          maskitoRef(node);
        };

        // onChange is left undefined below per the Maskito docs: https://maskito.dev/frameworks/react
        // From the Docs:
        // Maskito core is developed as framework-agnostic library. It does not depend on any JS-framework's peculiarities.
        // It uses only native browser API. That is why you should use native onInput instead of React-specific onChange event.
        // Do not worry, both events works similarly! Read more about it in the official React documentation (https://react.dev/reference/react-dom/components/input#props).

        const maskitoField = {
          ...field,
          ref,
          onInput: field.onChange,
          onChange: undefined,
          // Explicitly convert the value to string to satisfy Input's type requirements
          value: String(field.value || ''),
        };

        return (
          <FormControl isInvalid={!!error} onBlur={field.onBlur}>
            <Flex alignItems="center">
              {label && <FormLabel fontWeight="bold">{label}</FormLabel>}
              {tag}
            </Flex>
            <InputGroup>
              <Input
                type="text"
                background="var(--chakra-colors-chakra-body-bg)"
                placeholder={placeholder}
                isDisabled={isDisabled}
                {...maskitoField}
                data-1p-ignore // disables 1password suggestions
                {...inputProps}
              />

              {inputRightElement && (
                <InputRightElement>{inputRightElement}</InputRightElement>
              )}
            </InputGroup>
            <FormErrorMessage>{error?.message}</FormErrorMessage>
            {helperText && <FormHelperText>{helperText}</FormHelperText>}
          </FormControl>
        );
      }}
    />
  );
};
